import { useMemo, useState } from 'react';
import { CheckCheck, ChevronLeft, ChevronRight, Eye, EyeOff, Search } from 'lucide-react';
import { MARD_291, PALETTE_BRAND, filterPaletteByCode, readableTextColor } from '../core/palette';
import type { ColorStat, FocusMode, PatternProject, RunAssistMode } from '../types';

function buildStats(project: PatternProject, editing: boolean): ColorStat[] {
  const map = new Map<number, { count: number; completed: number }>();
  for (let i = 0; i < project.colorIndices.length; i += 1) {
    if (project.empty[i]) continue;
    const index = project.colorIndices[i];
    const item = map.get(index) || { count: 0, completed: 0 };
    item.count += 1;
    if (project.completed[i]) item.completed += 1;
    map.set(index, item);
  }
  const total = [...map.values()].reduce((sum, item) => sum + item.count, 0);
  const indexes = editing ? MARD_291.map((_, index) => index) : [...map.keys()];
  return indexes.map((paletteIndex) => {
    const item = map.get(paletteIndex) || { count: 0, completed: 0 };
    return {
      ...MARD_291[paletteIndex],
      paletteIndex,
      count: item.count,
      completed: item.completed,
      percent: total ? item.count / total * 100 : 0
    };
  }).sort((a, b) => a.code.localeCompare(b.code, 'en', { numeric: true, sensitivity: 'base' }));
}

export function ColorPanel({ project, selectedColor, focusMode, editing, onSelect, onFocusMode, onMarkColor }: {
  project: PatternProject;
  selectedColor: number | null;
  focusMode: FocusMode;
  editing: boolean;
  onSelect: (index: number) => void;
  onFocusMode: (mode: FocusMode) => void;
  onMarkColor: (index: number, completed: boolean) => void;
}) {
  const [query, setQuery] = useState('');
  const [runAssistMode, setRunAssistMode] = useState<RunAssistMode>('none');
  const stats = useMemo(() => buildStats(project, editing), [editing, project]);
  const displayBrand = project.paletteBrand || PALETTE_BRAND;
  const displayCode = (paletteIndex: number, fallback: string) => project.paletteCodeMap?.[String(paletteIndex)] || fallback;
  const visible = filterPaletteByCode(stats, query, (item) => editing ? item.code : displayCode(item.paletteIndex, item.code));
  const selectedPosition = stats.findIndex((item) => item.paletteIndex === selectedColor);
  const selected = stats[selectedPosition] || null;
  const total = stats.reduce((sum, item) => sum + item.count, 0);
  const completed = stats.reduce((sum, item) => sum + item.completed, 0);

  function move(step: number) {
    if (!stats.length) return;
    const index = selectedPosition < 0 ? 0 : (selectedPosition + step + stats.length) % stats.length;
    onSelect(stats[index].paletteIndex);
  }

  function changeRunAssistMode(mode: RunAssistMode) {
    setRunAssistMode(mode);
    window.dispatchEvent(new CustomEvent<RunAssistMode>('yxmpd-run-assist-change', { detail: mode }));
  }

  return (
    <aside className="color-panel">
      <div className="color-summary">
        <div><p className="eyebrow">施工进度</p><strong>{completed}<small> / {total} 颗</small></strong></div>
        <span>{total ? Math.round(completed / total * 100) : 0}%</span>
      </div>
      <div className="progress-track"><i style={{ width: `${total ? completed / total * 100 : 0}%` }} /></div>
      <div className="pattern-spec-grid" aria-label="图纸规格">
        <span><small>尺寸</small><strong>{project.width}×{project.height}</strong></span>
        <span><small>颜色</small><strong>{stats.length}</strong></span>
        <span><small>颗数</small><strong>{total.toLocaleString('zh-CN')}</strong></span>
      </div>

      <div className="focus-switch" aria-label="颜色显示方式">
        <button className={focusMode === 'all' ? 'active' : ''} type="button" onClick={() => onFocusMode('all')}><Eye size={16} />全部</button>
        <button className={focusMode === 'dim' ? 'active' : ''} type="button" onClick={() => onFocusMode('dim')}>淡化</button>
        <button className={focusMode === 'solo' ? 'active' : ''} type="button" onClick={() => onFocusMode('solo')}><EyeOff size={16} />单色</button>
      </div>

      {selected && (
        <div className="selected-color-card">
          <div className="selected-swatch" style={{ background: selected.hex, color: readableTextColor(selected.hex) }}>{displayCode(selected.paletteIndex, selected.code)}</div>
          <div><small><b className="palette-brand-badge">{displayBrand}</b> 当前颜色</small><strong>{displayCode(selected.paletteIndex, selected.code)}</strong><span>剩余 {selected.count - selected.completed} 颗</span></div>
          <div className="color-nav"><button type="button" onClick={() => move(-1)} aria-label="上一个颜色"><ChevronLeft size={18} /></button><button type="button" onClick={() => move(1)} aria-label="下一个颜色"><ChevronRight size={18} /></button></div>
          {!editing && <button className="complete-color" type="button" onClick={() => onMarkColor(selected.paletteIndex, selected.completed < selected.count)}><CheckCheck size={17} />{selected.completed < selected.count ? '完成当前色' : '取消完成'}</button>}
          {!editing && <div className="run-assist-control"><span>按方向查看</span><div role="group" aria-label="按方向查看当前色"><button className={runAssistMode === 'none' ? 'active' : ''} type="button" onClick={() => changeRunAssistMode('none')}>默认</button><button className={runAssistMode === 'horizontal' ? 'active' : ''} type="button" onClick={() => changeRunAssistMode('horizontal')}>横向</button><button className={runAssistMode === 'vertical' ? 'active' : ''} type="button" onClick={() => changeRunAssistMode('vertical')}>纵向</button></div></div>}
        </div>
      )}

      <div className="color-list-head">
        <div><h3>{editing ? '选择画笔颜色' : '色号清单'}</h3><span><b className="palette-brand-badge">{editing ? 'MARD' : displayBrand}</b>{editing ? '291 色' : `${stats.length} 种颜色`}</span></div>
        <label className="search-mini"><Search size={16} /><input aria-label={`搜索 ${displayBrand} 色号`} value={query} onChange={(event) => setQuery(event.target.value.replace(/[^a-z0-9]/gi, ''))} placeholder="如 A5" inputMode="text" autoCapitalize="characters" autoComplete="off" spellCheck={false} maxLength={8} /></label>
      </div>
      <div className="color-list">
        {visible.map((item) => {
          const done = item.completed === item.count;
          return (
            <button key={item.paletteIndex} className={`color-row ${selectedColor === item.paletteIndex ? 'active' : ''} ${done ? 'done' : ''}`} type="button" onClick={() => onSelect(item.paletteIndex)}>
              <span className="color-dot" style={{ background: item.hex }} />
              <span className="color-code"><strong><em>{editing ? 'MARD' : displayBrand}</em>{editing ? item.code : displayCode(item.paletteIndex, item.code)}</strong><small>{item.completed} / {item.count}</small></span>
              <span className="mini-progress"><i style={{ width: `${item.count ? item.completed / item.count * 100 : 0}%` }} /></span>
              <span className="color-count">{item.count}</span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
